/**
 * The words a deal estimate is read with (Estimate Tool 13): what each source, flag and basis
 * means to a reader. Pure, shared by the server and the client through public/js/lib/deal.js
 * so a screen and an export cannot disagree about what a flag says.
 */

/** Where a figure came from, in words. */
export const SOURCE_WORDS = Object.freeze({
  // The type
  hubspot: 'from HubSpot',
  keywords: 'from the words on the deal',
  none: 'not known',
  // Hours
  amount: 'the amount at the blended rate',
  analogs: 'the median of the analogs',
  canonical_type: 'the median of closed projects of this type',
  all_closed: 'the median of every closed project',
  // Amount
  deal: 'the deal amount',
  // Kick-off
  manual: 'set here',
  explicit: "HubSpot's own date",
  close_date_lag: 'the close date plus the lag',
  next_week: 'next planning week (the close date has passed)',
  // Delivery and duration
  inferred: 'kick-off plus the duration',
  type_tier: 'closed projects of this type and size',
  default: 'the default duration',
  // The role mix
  deal_services: "the deal's own service tags",
});

/** What each flag means, and whether it is a warning or a note. */
export const FLAG_META = Object.freeze({
  no_activity: {
    label: 'No activity',
    text: 'HubSpot holds nothing against this deal; the title is all there is.',
    level: 'note',
  },
  type_unknown: {
    label: 'Type unknown',
    text: 'Nothing on the deal names the kind of work.',
    level: 'warn',
  },
  type_ambiguous: {
    label: 'Type ambiguous',
    text: 'The words argue for more than one kind of work equally.',
    level: 'warn',
  },
  missing_amount: {
    label: 'No amount',
    text: 'The deal carries no amount; the analogs stand in for the dollars.',
    level: 'warn',
  },
  low_analog_count: {
    label: 'Few analogs',
    text: 'Fewer comparable projects than the answer wants.',
    level: 'warn',
  },
  no_analogs: {
    label: 'No analogs',
    text: 'No closed project resembles this deal above the floor.',
    level: 'warn',
  },
  no_role_mix: {
    label: 'No role mix',
    text: 'Nothing says which roles would do the work.',
    level: 'warn',
  },
  no_scope: {
    label: 'No scope',
    text: 'Neither the amount nor the analogs give an hour figure.',
    level: 'warn',
  },
  hours_from_analogs: {
    label: 'Hours from analogs',
    text: "No amount, so the hours are the analogs' median.",
    level: 'note',
  },
  hours_from_pool: {
    label: 'Hours from the pool',
    text: 'No amount and no analogs, so the hours are a pooled median.',
    level: 'note',
  },
  amount_from_analogs: {
    label: 'Amount from analogs',
    text: "No deal amount, so the dollars are the analogs' median gross.",
    level: 'note',
  },
  analogs_disagree: {
    label: 'Analogs disagree',
    text: "The analogs' hours and the amount's hours differ by more than the tolerance.",
    level: 'warn',
  },
  role_unstaffed: {
    label: 'Unstaffed role',
    text: 'A role nobody active has booked or holds as a discipline.',
    level: 'note',
  },
  missing_kickoff: {
    label: 'Kick-off derived',
    text: 'No kick-off date set; the close date plus the lag stands in.',
    level: 'note',
  },
  missing_close_date: {
    label: 'No close date',
    text: 'Nothing anchors a schedule.',
    level: 'warn',
  },
  close_date_past: {
    label: 'Close date past',
    text: 'The close date has passed; the schedule starts next planning week.',
    level: 'warn',
  },
  kickoff_past: {
    label: 'Kick-off past',
    text: 'The kick-off date given is already behind us.',
    level: 'warn',
  },
  inferred_delivery: {
    label: 'Delivery inferred',
    text: 'No delivery date; kick-off plus the duration stands in.',
    level: 'note',
  },
  no_task_history: {
    label: 'No task history',
    text: 'None of the analogs recorded a schedule, so there is no typical project to break the hours into.',
    level: 'warn',
  },
  low_task_coverage: {
    label: 'Tasks mostly bespoke',
    text: 'Less than half of the analogs’ hours sit in tasks most of them share; the rest is folded into Other lines.',
    level: 'note',
  },
});

/** Why a deal estimate has no task breakdown (Estimate Tool 13.9). */
export const TASKS_SKIPPED = Object.freeze({
  no_scope: 'Nothing sized the work, so there are no hours to break into tasks.',
  no_task_history: 'None of the analogs recorded a schedule.',
});

/** The words for a flag the table does not know, so a new flag reads as itself rather than crashing a screen. */
export function flagMeta(flag) {
  return FLAG_META[flag] ?? { label: String(flag).replace(/_/g, ' '), text: '', level: 'note' };
}

export function sourceWords(source) {
  return SOURCE_WORDS[source] ?? (source ? String(source).replace(/_/g, ' ') : '');
}

/** The reason a deal's estimate is stale, in a sentence. */
export function staleSentence(reasons) {
  const all = reasons ?? [];
  const list = all.filter((r) => r !== 'method').map((r) => r.replace(/_/g, ' '));
  // `method` is the tool's change, not the deal's (13.9).
  const method = all.includes('method')
    ? 'It was made by an older version of the estimating method.'
    : '';
  if (!list.length) return method;
  const words =
    list.length === 1 ? list[0] : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  const deal = `Since this was generated the deal's ${words} ${list.length === 1 ? 'has' : 'have'} changed.`;
  return method ? `${deal} ${method}` : deal;
}

/** The activity counts as one line: "2 emails, 1 meeting, 1 call". */
export function activitySentence(counted) {
  const names = { note: 'note', email: 'email', call: 'call', meeting: 'meeting', task: 'task' };
  const parts = Object.entries(counted ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${names[k] ?? k}${n === 1 ? '' : 's'}`);
  return parts.length ? parts.join(', ') : 'no activity';
}

/**
 * A quarter-hour figure as itself: 657.25 stays 657.25, 700 is 700, never "657.3". Every hour a
 * deal estimate produces is a multiple of 0.25 and the screen must let a reader see that.
 */
export function quarterHours(value, { unit = true } = {}) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const text = Number(value)
    .toFixed(2)
    .replace(/\.?0+$/, '');
  return unit ? `${text} h` : text;
}
